import { filter, pluck, take } from 'rxjs/operators'
import { EventEmitter, Injectable } from '@angular/core'
import { Apollo } from 'apollo-angular'
import { LocalStorageService } from '@mistergreen/shared/angular-injectables/local-storage/local-storage.service'
import { BehaviorSubject, Subject } from 'rxjs'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import {
    ExchangeForJsonWebTokenGQL,
    GetCurrentUserGQL,
    RequestLoginEmailGQL,
    SignupUserGQL,
    ValidateSignupUserGQL,
} from '../../../generated/graphql'
import { getQueryStringValue } from '../../utilities/getQueryStringValue'
import { timedPromise } from '../../utilities/timedPromise'
import { HubspotFormService } from '../../hubspot/form/hubspot-form.service'
import { getGclid, hasValidGclid } from '../../utilities/getGclid'

// remove current token from localStorage if key and token are present in url
try {
    const token = getQueryStringValue('token')
    const key = getQueryStringValue('key')
    if (key && token && <any>window && (<any>window).localStorage) {
        ;(<any>window).localStorage.removeItem('token')
    }
} catch {}

export type User = { id: string; email: string; roles: string[] }

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class AuthenticationService {
    currentUser: BehaviorSubject<null | User> = new BehaviorSubject(null)
    signupEmail: Subject<string> = new Subject()
    signupSuccessful: EventEmitter<{ id: string; email: string }> = new EventEmitter()
    loginSuccessful: EventEmitter<{ id: string; email: string }> = new EventEmitter()
    bootstrapRunner: Promise<unknown>
    private token: string
    private apolloHasInitialized: Promise<true>

    constructor(
        private apollo: Apollo,
        private localStorageService: LocalStorageService,
        private exchangeForJsonWebTokenGQL: ExchangeForJsonWebTokenGQL,
        private getCurrentUserGQL: GetCurrentUserGQL,
        private requestLoginEmailGQL: RequestLoginEmailGQL,
        private signupUserGQL: SignupUserGQL,
        private validateSignupUserGQL: ValidateSignupUserGQL,
        private hubspotFormService: HubspotFormService
    ) {
        this.apolloHasInitialized = new Promise(accept => {
            const acceptInterval = setInterval(() => {
                try {
                    this.apollo.getClient()
                    clearInterval(acceptInterval)
                    accept(true)
                } catch {}
            }, 10)
        })

        this.bootstrap()
    }

    get isLoggedIn(): boolean {
        return this.currentUser.getValue() !== null
    }

    async setToken(token) {
        if (this.token !== token && !(!token && !this.token)) {
            this.token = token
            let tokenHasChanged = false

            if (this.localStorageService.getItem('token') !== token) {
                this.localStorageService.setItem('token', token)
                tokenHasChanged = true
            }

            let currentUser: null | User = null

            if (token !== null) {
                try {
                    // remove cached entries (if client exits)
                    await this.apollo.client.cache.reset()
                } catch {
                    // client was not initialized yet
                    // wait until it has before proceeding
                    await this.apolloHasInitialized
                }

                try {
                    const {
                        data: {
                            getCurrentUser: { id, emailAddress, roles },
                        },
                    } = await this.getCurrentUserGQL.fetch().toPromise()

                    currentUser = <User>{ id, email: emailAddress, roles }
                } catch (e) {
                    console.log(e)
                    this.localStorageService.setItem('token', null)
                }
            }

            this.currentUser.next(currentUser || null)

            if (tokenHasChanged && currentUser) {
                this.loginSuccessful.emit(currentUser)
            }
        }
    }

    async requestLogin(emailAddress: string, path: string = '/') {
        await this.requestLoginEmailGQL
            .mutate({
                emailAddress,
                path,
            })
            .toPromise()
    }

    async signup(emailAddress: string): Promise<User> {
        this.signupEmail.next(emailAddress)
        await timedPromise(1)

        const [
            {
                data: { validateSignupUser },
            },
        ]: any = await Promise.all([
            // validate use on platform
            this.validateSignupUserGQL.fetch({ emailAddress }).toPromise(),

            // register user in hubspot (this makes sure session is tracked)
            this.hubspotFormService
                .submitForm(
                    'c14510f3-02e0-4507-b2cc-7c98d798a30a',
                    [
                        {
                            name: 'email',
                            value: emailAddress,
                        },
                        ...(hasValidGclid()
                            ? [
                                  {
                                      name: 'gclid',
                                      value: getGclid(),
                                  },
                              ]
                            : []),
                    ],
                    {
                        skipIdentify: true,
                        consentToProcess: true,
                        consentToEmail: true,
                    }
                )
                .catch(async () => undefined),
        ])

        if (!validateSignupUser) {
            throw new Error(`${emailAddress} already in use`)
        }

        const {
            data: {
                signupUser: { token },
            },
        }: any = await this.signupUserGQL.mutate({ emailAddress }).toPromise()

        // set authentication (triggers user login)
        this.localStorageService.setItem('token', token)

        // wait until user info has been loaded
        const user = await this.currentUser.pipe(filter<User>(Boolean), take(1)).toPromise()

        this.signupSuccessful.emit(user)

        return user
    }

    /**
     * Log Out user
     * It will remove the token from localStorage
     */
    logOut() {
        this.localStorageService.removeItem('token')
    }

    async bootstrap() {
        if (!this.bootstrapRunner) {
            this.bootstrapRunner = this.runBootstrap()
        }

        return this.bootstrapRunner
    }

    private async runBootstrap() {
        let token

        try {
            // try to get token and key directly from browser url
            const exchangeToken = getQueryStringValue('token')
            const exchangeKey = getQueryStringValue('key')

            if (exchangeToken && exchangeKey) {
                token = await this.exchangeForJsonWebToken(exchangeToken, exchangeKey)
            }
        } catch (e) {
            console.log(e)
        } finally {
            if (!token) {
                // otherwise, check if a token was saved to localstorage
                token = this.localStorageService.getItem('token')
            }

            if (token) {
                await this.setToken(token)
            }
        }

        // subscribe to token changes in localstorage
        // this will pickup token changes in other tabs/windows
        this.localStorageService
            .getItemObservable('token')
            .pipe(untilDestroyed(this))
            .subscribe(token => this.setToken(token))
    }

    private async exchangeForJsonWebToken(token, tokenId) {
        await this.apolloHasInitialized

        return this.exchangeForJsonWebTokenGQL
            .mutate({
                token,
                tokenId,
            })
            .pipe(pluck('data', 'exchangeForJsonWebToken', 'token'))
            .toPromise()
    }
}
